import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { queryClient } from '../api/queryClient';
import { hasAtLeast } from './roles';
import type { Membership, MeResponse } from './types';

interface TenantState {
  me: MeResponse | null;
  currentTenantId: string | null;

  setMe: (me: MeResponse | null) => void;
  setCurrentTenant: (id: string | null) => void;
  reset: () => void;
}

// Persisted under datahub.tenant.v1. Only currentTenantId is persisted; the
// `me` payload is refetched after every login so memberships stay fresh.
export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      me: null,
      currentTenantId: null,

      setMe: (me) => set({ me }),

      setCurrentTenant: (id) => {
        set({ currentTenantId: id });
        // Clear tenant-scoped caches so switching tenants doesn't leak data
        // across the React Query cache.
        queryClient.clear();
      },

      reset: () => set({ me: null, currentTenantId: null }),
    }),
    {
      name: 'datahub.tenant.v1',
      partialize: (state) => ({ currentTenantId: state.currentTenantId }),
    },
  ),
);

// Plain selectors — call as `currentMembership(useTenantStore.getState())` or
// `useTenantStore(currentMembership)`. Keeping them out of the store keeps the
// reactive surface small.
export function currentMembership(state: TenantState): Membership | null {
  if (!state.me || !state.currentTenantId) return null;
  return state.me.memberships.find((m) => m.tenantId === state.currentTenantId) ?? null;
}

export function currentTenantName(state: TenantState): string {
  const m = currentMembership(state);
  return m?.tenantName ?? '';
}

export function isSuperAdmin(state: TenantState): boolean {
  return state.me?.isSuperAdmin ?? false;
}

export function isTenantAdmin(state: TenantState): boolean {
  return state.currentTenantId !== null && hasAtLeast(state.me?.clientRole, 'admin');
}

// Picks a sensible default tenant for a freshly-hydrated user.
//   1) Persisted tenant id if it still matches an active membership.
//   2) First active membership.
//   3) null → caller renders the no-tenant-access screen. Super-admins get every
//      active tenant in `memberships`, so they only land there when no tenant
//      exists yet, with a path into /admin to create one.
export function pickDefaultTenantId(me: MeResponse, persisted: string | null): string | null {
  const active = me.memberships.filter((m) => m.status === 'active');

  if (persisted && active.some((m) => m.tenantId === persisted)) {
    return persisted;
  }
  if (active.length > 0) return active[0].tenantId;
  return null;
}
